import { ShoppingBag, ArrowRight } from 'lucide-react';
import { DashboardOrder } from '../../services/dashboard.service';
import { useCurrency } from '../../hooks/useCurrency';

interface OrdersSummaryProps {
  orders: DashboardOrder[];
  loading?: boolean;
  onNavigate?: (page: string) => void;
  onOrderClick?: (orderId: string) => void;
}

export function OrdersSummary({ orders, loading = false, onNavigate, onOrderClick }: OrdersSummaryProps) {
  const { formatCurrencySpaced } = useCurrency();

  // Get status badge color
  const getStatusBadge = (status: DashboardOrder['status']) => {
    const badges = {
      PENDING: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
      CONFIRMED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
      PREPARING: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
      READY: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
      DELIVERED: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
      CANCELLED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    };
    return badges[status] || badges.PENDING;
  };

  // Format status text
  const formatStatus = (status: string) => {
    return status.charAt(0) + status.slice(1).toLowerCase();
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 60) {
      return `${diffMins}m ago`;
    } else if (diffHours < 24) {
      return `${diffHours}h ago`;
    } else if (diffDays < 7) {
      return `${diffDays}d ago`;
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6" style={{ minHeight: '380px' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 p-2 rounded-xl">
            <ShoppingBag className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Recent Orders
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Latest customer orders
            </p>
          </div>
        </div>
        <button
          onClick={() => onNavigate?.('orders')}
          className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 text-sm font-medium flex items-center gap-1 transition-colors"
        >
          View All
          <ArrowRight className="w-4 h-4" />
        </button>
      </div>

      {/* Orders List */}
      <div className="space-y-3">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 5 }).map((_, i) => (
            <div
              key={i}
              className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50"
            >
              <div className="flex-1">
                <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mb-2" />
                <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
              </div>
              <div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            </div>
          ))
        ) : orders.length === 0 ? (
          // Empty state
          <div className="text-center py-8">
            <ShoppingBag className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              No recent orders
            </p>
          </div>
        ) : (
          // Orders
          orders.map((order) => (
            <div
              key={order.id}
              className="flex items-center justify-between p-4 rounded-xl bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              role="button"
              tabIndex={0}
              onClick={() => onOrderClick?.(order.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onOrderClick?.(order.id);
                }
              }}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <p className="font-medium text-gray-900 dark:text-white">
                    {order.orderNumber}
                  </p>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${getStatusBadge(
                      order.status
                    )}`}
                  >
                    {formatStatus(order.status)}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                  <span>{order.customerName}</span>
                  <span>•</span>
                  <span>{order.items} items</span>
                  <span>•</span>
                  <span>{formatDate(order.orderDate)}</span>
                </div>
              </div>
              <div className="text-right ml-4">
                <p className="font-semibold text-gray-900 dark:text-white">
                  {formatCurrencySpaced(order.total)}
                </p>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
